import React, { useMemo, useRef } from "react";
import { DownloadSimple, Sticker } from "@phosphor-icons/react";
import { toast } from "sonner";
import { downloadSVG, downloadPNG } from "@/lib/download";
import { buildStickerSVG } from "@/lib/svgBuilders";
import { getTierConfig } from "@/components/dashboard/FoundingBadge";

/**
 * CarSticker — print-ready circular window sticker.
 * SVG is built as a string (bypasses JSX expression wrappers) and injected
 * via dangerouslySetInnerHTML so the DOM is clean, valid SVG suitable for
 * canvas rasterization and print shops.
 */
export const CarSticker = ({ user }) => {
    const containerRef = useRef(null);
    const cfg = getTierConfig(user?.founder_tier);
    const number = user ? String(user.member_number).padStart(4, "0") : "0000";
    const filenameBase = `eviq-window-sticker-${number}`;

    const svgHtml = useMemo(
        () =>
            user
                ? buildStickerSVG({
                      color: cfg.color,
                      tierShort: cfg.short.toUpperCase(),
                      tierCap: cfg.cap.toUpperCase(),
                      memberNumber: number,
                      joined: new Date(user.member_since).getFullYear(),
                  })
                : "",
        [user, cfg.color, cfg.short, cfg.cap, number]
    );

    if (!user) return null;

    const getSvg = () => containerRef.current?.querySelector("svg");

    const handlePNG = async () => {
        await downloadPNG(getSvg(), `${filenameBase}.png`, {
            width: 600,
            height: 600,
            scale: 3,
        });
        toast.success("Sticker PNG saved", { description: "1800×1800 · transparent." });
    };
    const handleSVG = () => {
        downloadSVG(getSvg(), `${filenameBase}.svg`);
        toast.success("Sticker SVG saved", {
            description: "Print at any size — ideal for pro sticker shops.",
        });
    };

    return (
        <div
            data-testid="car-sticker"
            className="grid grid-cols-1 gap-6 rounded-lg border border-[#1f1f22] bg-[#0a0a0a] p-6 sm:p-8 md:grid-cols-[180px_1fr] md:items-center md:gap-8"
        >
            <div className="mx-auto md:mx-0">
                <div
                    ref={containerRef}
                    data-testid="sticker-preview"
                    className="rounded-2xl bg-[#050505] p-2 [&>svg]:h-[140px] [&>svg]:w-[140px]"
                    dangerouslySetInnerHTML={{ __html: svgHtml }}
                />
            </div>

            <div>
                <div className="flex items-center gap-2 text-zinc-500">
                    <Sticker size={14} weight="fill" color={cfg.color} />
                    <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em]">
                        Window sticker · Take EVIQ with you
                    </span>
                </div>
                <h3 className="mt-3 font-editorial text-xl leading-tight text-white sm:text-2xl">
                    A small, visible sign you're part of the movement.
                </h3>
                <p className="mt-2 text-sm text-zinc-500">
                    A rounded-square emblem echoing the EVIQ app icon — bold enough to spark a
                    conversation at a car show, refined enough to sit next to a marque decal.
                    Numbered to you and dated in Roman numerals.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                    <button
                        type="button"
                        data-testid="sticker-download-png-btn"
                        onClick={handlePNG}
                        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-black transition-transform duration-200 hover:-translate-y-0.5"
                        style={{ background: cfg.color }}
                    >
                        <DownloadSimple size={14} weight="bold" />
                        Download PNG
                    </button>
                    <button
                        type="button"
                        data-testid="sticker-download-svg-btn"
                        onClick={handleSVG}
                        className="inline-flex items-center gap-2 rounded-full border border-[#27272a] bg-[#0a0a0a] px-4 py-2 text-sm text-zinc-200 transition-colors duration-200 hover:border-[#3a3a3f] hover:text-white"
                    >
                        <DownloadSimple size={14} />
                        Print-ready SVG
                    </button>
                </div>
                <p className="mt-3 font-mono-metric text-[10px] uppercase tracking-[0.22em] text-zinc-600">
                    Recommended print size · 1 – 1.5 inches · rounded-square die-cut
                </p>
            </div>
        </div>
    );
};

export default CarSticker;
